import { formatDate } from "../../../helpers/utils";

export type UserSortKey = "email" | "role" | "expiresAt" | "sites";
export type SortDirection = "asc" | "desc";
export type SiteNames = Record<string, string>;

export function siteLabel(reference: string, siteNames: SiteNames): string {
    return siteNames[reference] || reference;
}

export function siteLabels(user: CoreUser, siteNames: SiteNames): string[] {
    return user.sites.map((ref) => siteLabel(ref, siteNames)).sort();
}

function searchText(user: CoreUser, siteNames: SiteNames): string {
    return [
        user.email,
        user.role,
        ...user.sites,
        ...siteLabels(user, siteNames),
        formatDate(user.expiresAt),
    ]
        .join(" ")
        .toLowerCase();
}

// Every whitespace-separated token must match at least one field.
export function filterUsers(
    users: CoreUser[],
    search: string,
    siteNames: SiteNames
): CoreUser[] {
    const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) {
        return users;
    }
    return users.filter((user) => {
        const haystack = searchText(user, siteNames);
        return tokens.every((token) => haystack.includes(token));
    });
}

// Missing/invalid dates sort as "never expires".
function expiryTime(user: CoreUser): number {
    const time = new Date(user.expiresAt + "Z").getTime();
    return isNaN(time) ? Infinity : time;
}

export function sortUsers(
    users: CoreUser[],
    key: UserSortKey,
    direction: SortDirection,
    siteNames: SiteNames
): CoreUser[] {
    const factor = direction === "desc" ? -1 : 1;

    const compare = (a: CoreUser, b: CoreUser): number => {
        switch (key) {
            case "role":
                return a.role.localeCompare(b.role);
            case "expiresAt": {
                const timeA = expiryTime(a);
                const timeB = expiryTime(b);
                return timeA === timeB ? 0 : timeA < timeB ? -1 : 1;
            }
            case "sites":
                return siteLabels(a, siteNames)
                    .join(" ")
                    .localeCompare(siteLabels(b, siteNames).join(" "));
            default:
                return a.email.localeCompare(b.email);
        }
    };

    return [...users].sort(
        (a, b) => factor * compare(a, b) || a.email.localeCompare(b.email)
    );
}
